import { FormInstance } from 'antd';
import React, { FC, useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Company, CompanyType } from 'api/models';
import { CreatableSelect } from 'components/atoms/select';
import { VirtualizedMenuList } from 'components/atoms/select/custom-components';
import { useAccount } from 'hooks/use-account';
import { useUser } from 'hooks/use-user';
import { useAllCompanies } from 'queries/account';
import { ProjectInfoStepFormValues } from '../project-info-step';
import { companyOption, getCompanyValue } from './utils';

interface CompanyCreatableSelectProps {
  onCreateSuccess?: (response: Company) => void;
  form: FormInstance<ProjectInfoStepFormValues>;
}

export const CompanyCreatableSelect: FC<CompanyCreatableSelectProps> = ({
  onCreateSuccess,
  form,
  ...restProps
}) => {
  const intl = useIntl();
  const { data: companies, isLoading } = useAllCompanies();
  const [userInput, setUserInput] = useState<string>('');
  const { company: vendorCompany } = useAccount();

  const customerCompanies = useMemo(
    () =>
      companies?.filter(
        (company) =>
          company.companyId !== vendorCompany.companyId &&
          company.companyType === CompanyType.Customer,
      ),
    [companies, vendorCompany],
  );

  const {
    company: { companyId },
  } = useUser();

  const handleCompanyCreate = (name: string) => {
    const selectedValue = getCompanyValue(
      name.trim(),
      vendorCompany.companyName,
      customerCompanies,
    );

    if (selectedValue && name) {
      setUserInput('');

      form.setFieldsValue({
        customer: selectedValue,
      });
    }
  };

  const onInputChange = (input, action) => {
    if (action.action !== 'input-blur' && action.action !== 'menu-close') {
      setUserInput(input);
    }
  };

  return (
    <CreatableSelect
      components={{
        DropdownIndicator: () => null,
        IndicatorSeparator: () => null,
        MenuList: VirtualizedMenuList,
      }}
      autoFocus
      options={(customerCompanies ?? [])
        .filter((c) => c.companyId !== companyId)
        .map(companyOption)}
      type="user"
      isClearable
      isLoading={isLoading}
      placeholder="Acme Inc."
      noOptionsMessage={() => (
        <FormattedMessage id="projects.create.project_details.customer_name.options.empty" />
      )}
      inputValue={userInput}
      onBlur={(e: React.ChangeEvent<HTMLInputElement>) =>
        handleCompanyCreate(e.target.value)
      }
      onInputChange={onInputChange}
      isValidNewOption={(input, val, options) =>
        !options.some((option) =>
          option.label.toLowerCase().includes(input.toLowerCase().trim()),
        )
      }
      onCreateOption={handleCompanyCreate}
      formatCreateLabel={(companyName) =>
        intl.formatMessage(
          { id: 'create.with.label' },
          {
            label: <strong>{companyName}</strong>,
          },
        )
      }
      {...restProps}
    />
  );
};
